import React from 'react';
import { Box, Typography, Spec, Row, Col, Image, formatPrice } from '@lucid/lego';
import { useUserIdentity } from '@lucid/portal-core';
import type { OrderDetailsResponse } from '../../../../../../api/services/lupp/order/dto.order';
import lucidCarImage from '../../../../../../assets/lucid_car.png';
import { useStore } from '../../../../../../store/store';
import {
  parseLocalePrice,
  resolveNlFeeLabel,
  resolveVatLabel,
} from '../../../../../../utils/tools';
import Price from '../../../../../../components/Checkout/VehicleCard/Price/Price';
import styles from './Configuration.module.scss';

type ConfigurationProps = {
  orderData: OrderDetailsResponse;
  theme: 'light' | 'dark';
  labels?: Record<string, string>;
  dataTestId?: string;
  isExpanded?: boolean;
};

/**
 * Check if trade-in support should be displayed based on payment method
 * Q4: UNCOMMENT to enable Trade-in Support for Q4
 * @param paymentMethod - The payment method to check
 * @returns true if payment method is eligible for trade-in support display
 */
// const isTradeInSupportEligible = (paymentMethod?: string | null): boolean => {
//   if (!paymentMethod) return false;
//   const lowerPaymentMethod = paymentMethod.toLowerCase();
//   return (
//     lowerPaymentMethod.includes('cash') ||
//     lowerPaymentMethod.includes('loan') ||
//     lowerPaymentMethod.includes('3rd party') ||
//     lowerPaymentMethod.includes('financing')
//   );
// };

/**
 * Configuration Details card component
 * Displays vehicle configuration information with image
 *
 * @component
 * @param {ConfigurationProps} props - Component props
 * @returns {JSX.Element} Configuration details card
 * @example
 * <Configuration orderData={orderData} theme="light" />
 */
const Configuration: React.FC<ConfigurationProps> = ({
  orderData,
  theme,
  labels,
  dataTestId = 'configuration-details',
  isExpanded = false,
}) => {
  const locale = useStore((state) => state.locale);
  const { userIdentity } = useUserIdentity();
  const isNlPricing = userIdentity?.countryCode?.toUpperCase() === 'NL';
  const configurationDetails = orderData?.configurationDetails;
  const isLfsLease = orderData?.preferredPaymentMethod?.toLowerCase() === 'lfs lease';
  // Q4: UNCOMMENT Trade-in Support check below to enable for Q4
  // const tradeInValue = configurationDetails?.tradeIn;
  // const hasTradeInSupport =
  //   tradeInValue !== null && tradeInValue !== undefined && Number(tradeInValue) !== 0;
  const chargingVoucherValue = configurationDetails?.chargingVoucher;
  const hasChargingVoucher =
    chargingVoucherValue !== null &&
    chargingVoucherValue !== undefined &&
    Number(chargingVoucherValue) !== 0;

  const chargingVoucherCreditValue = configurationDetails?.chargingVoucherCredit;
  const hasChargingVoucherCredit =
    chargingVoucherCreditValue !== null &&
    chargingVoucherCreditValue !== undefined &&
    Number(chargingVoucherCreditValue) !== 0;

  const vehicleImageUri = configurationDetails?.imageUrl || lucidCarImage;

  return (
    <Box pb={8} data-testid={dataTestId}>
      {!isExpanded && (
        <Row gutter={[24, 32]} align="middle" justify="center" id="configuration-overview-row">
          <Col sm={12} xl={8}>
            <Image
              uri_mobile={{ '1x': vehicleImageUri, '2x': vehicleImageUri }}
              uri_tablet={{ '1x': vehicleImageUri, '2x': vehicleImageUri }}
              uri_desktop={{ '1x': vehicleImageUri, '2x': vehicleImageUri }}
              alt={orderData.model || 'Vehicle'}
              className={styles.overviewImage}
              theme={theme}
              rounded={true}
            />
          </Col>
          <Col sm={12} xl={4}>
            <Row gutter={[16, 0]}>
              <Col sm={4} xl={12}>
                <Spec
                  variant="gray"
                  theme="light"
                  size="extraSmall"
                  type="sans"
                  eyebrow={labels?.['modelYear'] ?? 'Model Year'}
                  title={configurationDetails?.year || '-'}
                />
              </Col>
              <Col sm={4} xl={12}>
                <Spec
                  variant="gray"
                  theme="light"
                  size="extraSmall"
                  type="sans"
                  eyebrow={labels?.['modelTrim'] ?? 'Model/Trim'}
                  title={configurationDetails?.trim || configurationDetails?.model || '-'}
                />
              </Col>
              <Col sm={4} xl={12}>
                <Spec
                  variant="gray"
                  theme="light"
                  size="extraSmall"
                  type="sans"
                  eyebrow={labels?.['vin'] ?? 'VIN'}
                  title={orderData?.vin || configurationDetails?.currentVin || '-'}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}
      {isExpanded && (
        <Row gutter={[24, 32]} align="middle" justify="center" id="configuration-pricing-row">
          <Col sm={12} xl={12}>
            <Image
              uri_mobile={{ '1x': vehicleImageUri, '2x': vehicleImageUri }}
              uri_tablet={{ '1x': vehicleImageUri, '2x': vehicleImageUri }}
              uri_desktop={{ '1x': vehicleImageUri, '2x': vehicleImageUri }}
              alt={configurationDetails?.model || 'Vehicle'}
              className={styles.pricingImage}
              theme={theme}
              rounded={true}
            />
          </Col>
          <Col sm={12} xl={12}>
            <Box pr={8} pl={8}>
              <Row gutter={[16, 8]}>
                {configurationDetails?.configurationItems?.map((item) => (
                  <Col sm={12} xl={12} key={`config-item-${item.name}`}>
                    <Row gutter={[16, 0]}>
                      <Col sm={8} xl={8}>
                        <Typography variant="body" size="extraSmall" theme={theme} color="black">
                          {item.name}
                        </Typography>
                      </Col>
                      <Col sm={4} xl={4}>
                        <Typography
                          variant="body"
                          size="extraSmall"
                          theme={theme}
                          color="black"
                          className={styles.rightAlign}
                        >
                          {typeof item.label === 'string'
                            ? item.label
                            : formatPrice(item.label, { locale })}
                        </Typography>
                      </Col>
                    </Row>
                  </Col>
                ))}
              </Row>

              <hr className={styles.divider} />
              <Price
                label={
                  labels?.['vehicle.configurationPriceExclVat'] ?? 'Configuration Price excl VAT'
                }
                value={parseLocalePrice(configurationDetails?.totalConfigPriceExclTax)}
                theme={theme}
                isBold
                skipFormatting
              />
              <hr className={styles.divider} />

              {/* Q4: UNCOMMENT Trade-in Support display below to enable for Q4 */}
              {/* hasTradeInSupport && isTradeInSupportEligible(orderData?.preferredPaymentMethod) && (
                <Row gutter={[16, 8]} className={styles.rowBottomGap}>
                  <Col sm={12} xl={12}>
                    <Row gutter={[16, 0]}>
                      <Col sm={8} xl={8}>
                        <Typography variant="body" size="extraSmall" theme={theme} color="black">
                          {labels?.['vehicle.tradeInSupport'] ?? 'Trade-in Support'}
                        </Typography>
                      </Col>
                      <Col sm={4} xl={4}>
                        <Typography
                          variant="body"
                          size="extraSmall"
                          theme={theme}
                          color="black"
                          className={styles.rightAlign}
                        >
                          {formatPrice(configurationDetails?.tradeIn, { locale })}
                        </Typography>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              ) */}

              {hasChargingVoucher && !isLfsLease && (
                <Price
                  label={labels?.['vehicle.chargingCredit'] ?? 'Charging Credit'}
                  value={Number(chargingVoucherValue)}
                  theme={theme}
                  skipFormatting
                />
              )}

              {hasChargingVoucherCredit && !isLfsLease && (
                <Price
                  label={labels?.['vehicle.chargingCreditOffer'] ?? 'Charging Credit Offer'}
                  value={Number(chargingVoucherCreditValue)}
                  theme={theme}
                  isNegative
                  skipFormatting
                />
              )}

              {!isLfsLease && (
                <Price
                  label={labels?.['vehicle.priceAdjustment'] ?? 'Price Adjustment'}
                  value={Number(configurationDetails?.priceAdjustment || 0)}
                  theme={theme}
                  isNegative
                  skipFormatting
                />
              )}

              {isNlPricing &&
                Object.entries(configurationDetails?.feesTaxable ?? {}).map(([key, value]) => (
                  <Price
                    key={`taxable-fee-${key}`}
                    label={resolveNlFeeLabel(key, labels)}
                    value={value}
                    theme={theme}
                    skipFormatting
                  />
                ))}

              <Price
                label={labels?.['vehicle.nlSubtotalExclVat'] ?? 'Subtotal excl VAT'}
                value={parseLocalePrice(
                  configurationDetails?.subTotalOrderExclTax ??
                    configurationDetails?.totalConfigPriceExclTax
                )}
                theme={theme}
                isBold
                skipFormatting
              />
              <Price
                label={`${resolveVatLabel(labels)} (${configurationDetails?.taxPercentage || 0}%)`}
                value={Number(configurationDetails?.taxValue || 0)}
                theme={theme}
                skipFormatting
              />

              {isNlPricing &&
                Object.entries(configurationDetails?.feesNonTaxable ?? {}).map(([key, value]) => (
                  <Price
                    key={`non-taxable-fee-${key}`}
                    label={resolveNlFeeLabel(key, labels)}
                    value={value}
                    theme={theme}
                    skipFormatting
                  />
                ))}

              <hr className={styles.divider} />
              <Price
                label={labels?.['vehicle.totalInclVat'] ?? 'Total incl VAT*'}
                value={parseLocalePrice(configurationDetails?.total)}
                theme={theme}
                isBold
                skipFormatting
              />
              <Box pt={16}>
                <Typography variant="body" size="extraSmall" theme={theme} color="gray-5">
                  {labels?.['vehicle.totalNote'] ??
                    '* Total price including VAT, destination, roadworthy, delivery, documentation and registration costs.'}
                </Typography>
              </Box>
            </Box>
          </Col>
        </Row>
      )}
    </Box>
  );
};

Configuration.displayName = 'Configuration';

export default Configuration;
